/**
 * Ten at a time, and a button for the rest.
 *
 * A list here is whatever the server sent - two hundred friends, every member
 * of a crew - and all of it was being built at once. Each row is an avatar
 * that decodes, a presence dot, a name that may be wearing an animated
 * gradient, and a menu waiting to be opened; two hundred of those is a
 * second of nothing on a page that should have been instant, and it is paid
 * again every time the list re-renders.
 *
 * So a list shows ten and says how many are behind that. Pressing the button
 * adds another ten. Nothing is fetched - the rows were always there - so this
 * costs one render of ten more rows rather than a request.
 *
 * Filtering or sorting puts it back to ten, because the first ten of a search
 * you just typed are the ten you wanted.
 */

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export const PAGE = 10;

/**
 * The slice to draw, and what to do about the rest.
 *
 * `key` is whatever decides this is a different list - the tab, the search
 * text, the sort. Change it and the count goes back to the first page.
 */
export function usePaged<T>(list: T[], key: unknown = '', step = PAGE) {
  const [shown, setShown] = useState(step);

  useEffect(() => { setShown(step); }, [key, step]);

  const slice = useMemo(() => list.slice(0, shown), [list, shown]);
  const rest = Math.max(0, list.length - shown);

  return {
    slice,
    rest,
    /** How many more the next press would add. */
    next: Math.min(rest, step),
    more: () => setShown((n) => n + step),
    all: () => setShown(list.length),
    reset: () => setShown(step)
  };
}

/**
 * The control under a shortened list.
 *
 * Says the real number rather than "more", because "47 more friends" is the
 * thing you actually want to know, and renders nothing at all when the whole
 * list is already on screen.
 */
export function ShowMore({ rest, next, noun, onMore, onAll, className }: {
  rest: number;
  next: number;
  /** Singular; pluralised here. "friend" gives "47 more friends". */
  noun: string;
  onMore: () => void;
  /** Offered beside it once there are more than one page left. */
  onAll?: () => void;
  className?: string;
}) {
  if (rest <= 0) return null;
  const word = rest === 1 ? noun : noun + 's';

  return (
    <div className={cn('flex items-center justify-center gap-2 pt-1', className)}>
      <button
        type="button"
        onClick={onMore}
        className={cn(
          'flex items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 py-1.5',
          'text-[12.5px] font-bold text-dim outline-none',
          'transition-[background-color,color] duration-[var(--t-hover)]',
          'hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]'
        )}
      >
        <Icon name="chevronD" size={13} />
        Show {next} more
        <span className="font-medium text-ghost">· {rest} {word} left</span>
      </button>
      {onAll && rest > next ? (
        <button
          type="button"
          onClick={onAll}
          className={cn(
            'rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-1.5 text-[12.5px] font-semibold text-mute outline-none',
            'transition-[background-color,color] duration-[var(--t-hover)]',
            'hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]'
          )}
        >
          Show all
        </button>
      ) : null}
    </div>
  );
}
